import { ChangeEvent, DragEvent, useState } from 'react'
import { LoaderFunctionArgs, useLoaderData, useParams, useRevalidator } from 'react-router-dom'
import { useCurrentHealthCenter, useIsAccountClosed } from '../../components/AppShell'
import { Button, ButtonAnchor, CheckboxField, EmptyState, Modal, PageHeading, Panel, PanelBody, useConfirm } from '../../components/common'
import { CrFile, CrFilesIndex, deleteCrFiles, fetchCrFiles, uploadCrFile } from '../../api/crFiles'
import { ApiError } from '../../lib/api'
import { trackEvent } from '../../lib/config'
import styles from './CrFilesPage.module.scss'

const MAX_FILESIZE_MB = 5

type UploadMethod = 'drop' | 'picker'

type UploadStatus = 'uploading' | 'uploaded' | 'tooLarge' | 'notAllowed' | 'failed'

type UploadOutcome = {
  readonly file: File
  readonly status: UploadStatus
}

const STATUS_DISPLAY: Readonly<Record<UploadStatus, { readonly icon: string; readonly message: string }>> = {
  uploading: { icon: 'hourglass_empty', message: 'Subiendo…' },
  uploaded: { icon: 'check_circle', message: 'Subido' },
  tooLarge: { icon: 'error', message: 'El archivo es demasiado grande' },
  notAllowed: { icon: 'error', message: 'Este tipo de archivo no está permitido' },
  failed: { icon: 'error', message: 'No se pudo subir, inténtalo de nuevo' },
}

function UploadModal({
  open,
  onClose,
  onUpload,
  outcomes,
}: {
  open: boolean
  onClose: () => void
  onUpload: (files: readonly File[], method: UploadMethod) => void
  outcomes: readonly UploadOutcome[]
}) {
  const [isDragging, setDragging] = useState(false)

  const takeFiles = (files: FileList | null, method: UploadMethod) => {
    if (files && files.length > 0) {
      onUpload(Array.from(files), method)
    }
  }

  const handleDrop = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault()
    setDragging(false)
    takeFiles(event.dataTransfer.files, 'drop')
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Subir nuevos archivos"
      footer={
        <Button variant="default" onClick={onClose}>
          Listo
        </Button>
      }
    >
      <label
        data-testid="drop-zone"
        className={`${styles.dropZone} ${isDragging ? styles.dropZoneActive : ''}`}
        onDragOver={(event) => {
          event.preventDefault()
          setDragging(true)
        }}
        onDragLeave={(event) => {
          if (!(event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget))) {
            setDragging(false)
          }
        }}
        onDrop={handleDrop}
      >
        <i className={`material-icons ${styles.dropIcon}`} aria-hidden="true">cloud_upload</i>
        <span className={styles.dropTitle}>Arrastra los archivos aquí</span>
        <span className={styles.dropHint}>
          o <span className={styles.browse}>haz click para elegirlos</span>
        </span>
        <span className={styles.dropLimit}>Máximo {MAX_FILESIZE_MB} MB por archivo</span>
        <input
          type="file"
          multiple
          className={styles.picker}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            takeFiles(event.target.files, 'picker')
            event.target.value = ''
          }}
        />
      </label>
      {outcomes.length > 0 && (
        <ul className={styles.outcomes}>
          {outcomes.map(({ file, status }, index) => (
            <li key={index} className={`${styles.outcome} ${styles[status]}`}>
              <i className="material-icons" aria-hidden="true">{STATUS_DISPLAY[status].icon}</i>
              <span className={styles.outcomeText}>
                <strong>{file.name}</strong>: {STATUS_DISPLAY[status].message}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  )
}

function FileCard({ crFile, isSelected, onToggle }: { crFile: CrFile; isSelected: boolean; onToggle: () => void }) {
  const closed = useIsAccountClosed()
  return (
    <div className={`${styles.card} ${isSelected ? styles.cardSelected : ''}`}>
      <a
        href={crFile.url}
        className={styles.thumbnail}
        onClick={() => trackEvent('Document Opened', undefined, { transport: 'sendBeacon' })}
      >
        <img src={crFile.image_url} alt={crFile.name ?? ''} loading="lazy" />
      </a>
      <div className={styles.cardBody}>
        {!closed && crFile.modifiable && (
          <CheckboxField
            className={styles.selectFile}
            label=""
            aria-label={`Seleccionar ${crFile.name ?? 'archivo'}`}
            checked={isSelected}
            onChange={onToggle}
          />
        )}
        <div className={styles.fileMeta}>
          <p className={styles.fileName} title={crFile.name ?? undefined}>
            {crFile.name}
          </p>
          <p className={styles.fileDate}>{crFile.created_at}</p>
        </div>
      </div>
    </div>
  )
}

export function loader({ params }: LoaderFunctionArgs): Promise<CrFilesIndex> {
  return fetchCrFiles(Number(params.healthCenterId), params.patientId as string, params.recordId as string)
}

export default function CrFilesPage() {
  const healthCenter = useCurrentHealthCenter()
  const closed = useIsAccountClosed()
  const params = useParams()
  const confirm = useConfirm()
  const patientId = params.patientId ?? ''
  const clinicalRecordId = params.recordId ?? ''
  const data = useLoaderData() as Awaited<ReturnType<typeof loader>>
  const { revalidate } = useRevalidator()
  const [selected, setSelected] = useState<readonly number[]>([])
  const [uploadOpen, setUploadOpen] = useState(false)
  const [outcomes, setOutcomes] = useState<readonly UploadOutcome[]>([])

  if (!healthCenter) {
    return null
  }

  const openUploadModal = () => {
    trackEvent('Upload Modal Opened')
    setUploadOpen(true)
  }

  const setStatus = (file: File, status: UploadStatus) =>
    setOutcomes((current) => current.map((outcome) => (outcome.file === file ? { ...outcome, status } : outcome)))

  const upload = async (files: readonly File[], method: UploadMethod) => {
    const isTooBig = (file: File) => file.size > MAX_FILESIZE_MB * 1024 * 1024
    setOutcomes((current) => [...current, ...files.map((file): UploadOutcome => ({ file, status: isTooBig(file) ? 'tooLarge' : 'uploading' }))])
    files.filter(isTooBig).forEach(() => trackEvent('Record File Rejected', { reason: 'too_large' }))

    const uploadable = files.filter((file) => !isTooBig(file))
    const results = await Promise.all(
      uploadable.map(async (file) => {
        try {
          await uploadCrFile(healthCenter.id, patientId, clinicalRecordId, file)
          setStatus(file, 'uploaded')
          return true
        } catch (error) {
          const isNotAllowed = error instanceof ApiError && error.status === 422
          setStatus(file, isNotAllowed ? 'notAllowed' : 'failed')
          if (isNotAllowed) {
            trackEvent('Record File Rejected', { reason: 'not_allowed' })
          }
          return false
        }
      }),
    )
    const uploadedCount = results.filter(Boolean).length
    if (uploadedCount > 0) {
      trackEvent('Record Files Uploaded', { count: uploadedCount, method })
      void revalidate()
    }
  }

  const removeSelected = async () => {
    const count = selected.length
    const isConfirmed = await confirm({
      title: 'Eliminar archivos',
      message: count === 1 ? '¿Eliminar el archivo seleccionado? Esto es irreversible.' : `¿Eliminar los ${count} archivos seleccionados? Esto es irreversible.`,
    })
    if (!isConfirmed) {
      return
    }
    await deleteCrFiles(healthCenter.id, clinicalRecordId, selected)
    trackEvent('Record Files Deleted', { count })
    setSelected([])
    void revalidate()
  }

  const toggle = (id: number) => setSelected(selected.includes(id) ? selected.filter((current) => current !== id) : [...selected, id])

  return (
    <Panel style={{ marginTop: 15 }}>
      <PanelBody>
        <PageHeading level={2}>{`Archivos de ${data.patient.name ?? ''}`}</PageHeading>

        {data.cr_files.length === 0 ? (
          <EmptyState
            id="no-cr_file"
            message={
              <>
                <i className={`material-icons ${styles.emptyIcon}`} aria-hidden="true">
                  folder_open
                </i>
                No hay archivos
              </>
            }
          >
            {!closed && (
              <div>
                <Button onClick={openUploadModal}>
                  <i className="material-icons" aria-hidden="true">cloud_upload</i> Subir nuevos archivos
                </Button>
              </div>
            )}
          </EmptyState>
        ) : (
          <>
            {!closed && (
              <div className={styles.toolbar}>
                <Button onClick={openUploadModal}>
                  <i className="material-icons" aria-hidden="true">cloud_upload</i> Subir nuevos archivos
                </Button>
                <Button variant="danger" disabled={selected.length === 0} onClick={() => void removeSelected()}>
                  <i className="material-icons" aria-hidden="true">delete</i>
                  {selected.length === 0 ? ' Eliminar archivos seleccionados' : ` Eliminar seleccionados (${selected.length})`}
                </Button>
              </div>
            )}
            <div id="cr_file-contents" className={styles.grid}>
              {data.cr_files.map((crFile) => (
                <FileCard key={crFile.id} crFile={crFile} isSelected={selected.includes(crFile.id)} onToggle={() => toggle(crFile.id)} />
              ))}
            </div>
          </>
        )}

        <UploadModal
          open={uploadOpen}
          onClose={() => {
            setUploadOpen(false)
            setOutcomes([])
          }}
          onUpload={(files, method) => void upload(files, method)}
          outcomes={outcomes}
        />

        <div className={styles.footer}>
          <ButtonAnchor href={data.back_path} variant="default" className="waves-effect">
            <i className="material-icons" aria-hidden="true">arrow_back</i> Volver
          </ButtonAnchor>
        </div>
      </PanelBody>
    </Panel>
  )
}
